import posthog from 'posthog-js';

class AnalyticsService {
  private initialized = false;

  init() {
    const posthogKey = process.env.REACT_APP_POSTHOG_KEY;
    const posthogHost = process.env.REACT_APP_POSTHOG_HOST || 'https://app.posthog.com';

    if (posthogKey) {
      posthog.init(posthogKey, {
        api_host: posthogHost,
        autocapture: true,
        capture_pageview: true,
        disable_session_recording: false,
        person_profiles: 'identified_only',
      });
      this.initialized = true;
      console.log('PostHog analytics initialized');
    } else {
      console.warn('PostHog API key not found, analytics disabled');
    }
  }

  identify(userId: string, properties?: Record<string, any>) {
    if (!this.initialized) return;
    posthog.identify(userId, properties);
  }

  track(event: string, properties?: Record<string, any>) {
    if (!this.initialized) return;
    posthog.capture(event, properties);
  }

  // User-related events
  trackUserRegistered(email: string) {
    this.track('User Registered', { email });
  }

  trackUserLoggedIn(email: string) {
    this.track('User Logged In', { email });
  }

  trackUserLoggedOut() {
    this.track('User Logged Out');
  }

  // Course-related events
  trackCourseCreated(courseId: number, courseName: string) {
    this.track('Course Created', {
      course_id: courseId,
      course_name: courseName,
    });
  }

  // Study session events
  trackStudySessionCreated(sessionId: number, courseId: number, title: string) {
    this.track('Study Session Created', {
      session_id: sessionId,
      course_id: courseId,
      title,
    });
  }

  trackStudySessionViewed(sessionId: number) {
    this.track('Study Session Viewed', {
      session_id: sessionId,
    });
  }

  // Upload events
  trackAudioUploaded(sessionId: number, fileSize: number, fileType: string) {
    this.track('Audio Uploaded', {
      session_id: sessionId,
      file_size: fileSize,
      file_type: fileType,
    });
  }

  trackDocumentUploaded(sessionId: number, fileSize: number, fileType: string) {
    this.track('Document Uploaded', {
      session_id: sessionId,
      file_size: fileSize,
      file_type: fileType,
    });
  }

  // Transcription management events
  trackTranscriptionDeleted(transcriptionId: number, sessionId: number, status: string, wasProcessing: boolean) {
    this.track('Transcription Deleted', {
      transcription_id: transcriptionId,
      session_id: sessionId,
      status: status,
      was_processing: wasProcessing,
    });
  }

  trackTranscriptionCancelled(transcriptionId: number, sessionId: number) {
    this.track('Transcription Cancelled', {
      transcription_id: transcriptionId,
      session_id: sessionId,
    });
  }

  // Study materials events
  trackMaterialsGenerated(sessionId: number, materialTypes: string[]) {
    this.track('Study Materials Generated', {
      session_id: sessionId,
      material_types: materialTypes,
      material_count: materialTypes.length,
    });
  }

  trackMaterialViewed(sessionId: number, materialType: string) {
    this.track('Study Material Viewed', {
      session_id: sessionId,
      material_type: materialType,
    });
  }

  // Questions events
  trackQuestionsGenerated(sessionId: number, questionTypes: string[], numQuestions: number) {
    this.track('Practice Questions Generated', {
      session_id: sessionId,
      question_types: questionTypes,
      num_questions: numQuestions,
    });
  }

  trackQuestionAnswered(questionId: number, isCorrect: boolean, questionType: string) {
    this.track('Question Answered', {
      question_id: questionId,
      is_correct: isCorrect,
      question_type: questionType,
    });
  }

  trackQuestionSkipped(questionId: number, questionType: string) {
    this.track('Question Skipped', {
      question_id: questionId,
      question_type: questionType,
    });
  }

  // Chat events
  trackChatOpened(sessionId: number) {
    this.track('Chat Opened', {
      session_id: sessionId,
    });
  }

  trackChatMessageSent(sessionId: number, messageLength: number) {
    this.track('Chat Message Sent', {
      session_id: sessionId,
      message_length: messageLength,
    });
  }

  // New methods for tracking actual chat messages
  trackChatUserMessage(sessionId: number, message: string) {
    this.track('Chat User Message', {
      session_id: sessionId,
      message: message,
      message_length: message.length,
    });
  }

  trackChatAIMessage(sessionId: number, message: string, userMessage: string) {
    this.track('Chat AI Message', {
      session_id: sessionId,
      ai_message: message,
      user_message: userMessage,
      ai_message_length: message.length,
      user_message_length: userMessage.length,
    });
  }

  // Chat message feedback tracking
  trackChatMessageLiked(sessionId: number, messageIndex: number, messageContent: string, userMessage: string) {
    this.track('Chat Message Liked', {
      session_id: sessionId,
      message_index: messageIndex,
      message_content: messageContent,
      user_message: userMessage,
    });
  }

  trackChatMessageDisliked(sessionId: number, messageIndex: number, messageContent: string, userMessage: string) {
    this.track('Chat Message Disliked', {
      session_id: sessionId,
      message_index: messageIndex,
      message_content: messageContent,
      user_message: userMessage,
    });
  }

  // UI events
  trackTabChanged(fromTab: string, toTab: string) {
    this.track('Tab Changed', {
      from_tab: fromTab,
      to_tab: toTab,
    });
  }

  trackFeatureUsed(feature: string, context?: Record<string, any>) {
    this.track('Feature Used', {
      feature,
      ...context,
    });
  }

  // Error tracking
  trackError(error: string, context?: Record<string, any>) {
    this.track('Error Occurred', {
      error,
      ...context,
    });
  }

  // Page tracking
  trackPageView(page: string) {
    if (!this.initialized) return;
    posthog.capture('$pageview', { page });
  }

  // Reset user session (for logout)
  reset() {
    if (!this.initialized) return;
    posthog.reset();
  }

  // Feedback chat events
  trackFeedbackChatOpened() {
    this.track('Feedback Chat Opened');
  }

  trackFeedbackMessage(message: string, isUser: boolean, sessionKey?: string) {
    if (isUser) {
      this.track('Feedback User Message', {
        message: message,
        message_length: message.length,
        session_key: sessionKey
      });
    } else {
      this.track('Feedback AI Response', {
        message: message,
        message_length: message.length,
        session_key: sessionKey
      });
    }
  }

  trackFeedbackSessionCompleted(messageCount: number, sessionKey?: string) {
    this.track('Feedback Session Completed', {
      total_messages: messageCount,
      session_key: sessionKey
    });
  }
}

// Export singleton instance
export const analytics = new AnalyticsService(); 